import { ArtButton } from '@art/shared-ui';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import RocketLaunchRounded from '@mui/icons-material/RocketLaunchRounded';
import styles from '../onboarding.module.scss';
import { SuccessMark } from '../../utils/onBoarding_svg';
import { useAppSwitch } from '../../header/app-switch/app-switch';
import { SwitchableApp } from '../../header/app-switch/app-switch.types';
import { productList } from '../../header/menu-list';
import { usePermissionsContext } from '../../../global/permissions';
import { productLanding } from '../../auth-check/product-landing';

type Product = {
  key: string;
  to: string;
  iconSrc: string;
  name: string;
  external?: boolean;
  appKey?: string;
  applicationCode?: string;
};

const PRODUCTS: Product[] = [
  {
    key: 'account-security',
    to: '/account-settings/general/accounts',
    iconSrc: '/assets/images/A-Realtime-Tech-short-logo.svg',
    name: 'Password & MFA',
    appKey: 'art-account-settings',
    applicationCode: 'ACCOUNT_SETTINGS',
  },
  {
    key: 'art-agent-lab',
    to: '/agent-builder/dashboard',
    iconSrc: '/assets/images/agent-lab-short.svg',
    name: 'Agent Lab',
    appKey: 'art-agent',
    applicationCode: 'AGENT_LAB',
  },
  {
    key: 'art-live',
    to: '/live/dashboard',
    iconSrc: '/assets/images/live-connect-short.svg',
    name: 'Live Connect',
    appKey: 'art-live',
    applicationCode: 'LIVE_CONNECT',
  },
  {
    key: 'art-docs',
    to: 'https://docs.arealtimetech.com/',
    // docs_logo.png still carries the retired circular mark
    iconSrc: '/assets/images/A-Realtime-Tech-short-logo.svg',
    name: 'A Realtime Tech Docs',
    external: true,
  },
];

const CompleteSetup = () => {
  const { switchApp } = useAppSwitch();
  const { hasApplication } = usePermissionsContext();
  const defaultPath = productLanding(hasApplication);
  const defaultProduct = PRODUCTS.find((product) => product.to === defaultPath) ?? PRODUCTS[1];

  const launch = (appKey: string, to: string) => {
    const app = (productList as SwitchableApp[]).find((p) => p.key === appKey);
    switchApp({ ...(app || { key: appKey, title: 'Console' }), to });
  };

  const open = ({ to, external, appKey }: Product) => {
    if (external) {
      window.open(to, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!appKey || to.startsWith('/account-settings/')) {
      window.location.href = to;
      return;
    }
    launch(appKey as string, to);
  };

  return (
    <section className={`${styles.done} ${styles.reveal}`}>
      <SuccessMark />

      <span className={styles.eyebrow}>
        <RocketLaunchRounded />
        All set
      </span>

      <h1 className={styles.display}>
        Your workspace is <span className={styles.accent}>ready.</span>
      </h1>

      <p className={styles.lede}>
        Your project, keys and channels are set up. Time to start building.
      </p>

      <div className={styles.actions}>
        <ArtButton
          label="Get Started"
          dataQa="onboarding-launch-console"
          fullWidth={false}
          onClick={() => open(defaultProduct)}
          sx={{ px: 4, py: 1.15 }}
        />
      </div>

      <div className={styles.done_links}>
        {PRODUCTS.map((item) => (
          <button
            type="button"
            key={item.key}
            className={styles.done_link}
            onClick={() => open(item)}
            data-qa={item.key}
          >
            <img src={item.iconSrc} alt="" />
            {item.name}
            {item.external && <OpenInNewRounded />}
          </button>
        ))}
      </div>
    </section>
  );
};

export default CompleteSetup;
